import { useState } from "react";
import PropTypes from "prop-types";
import "./application-summary.css";
import "./sidemenu.css";
import { Box, Typography } from "@mui/material";
import { ArrowDropDownRounded, ArrowDropUpRounded } from "@mui/icons-material";

const USAGE_ITEMS = [
  { label: "Activity", activeValue: "activity", changeValue: "activity" },
  { label: "Sessions", activeValue: "sessions", changeValue: "sessions" },
  { label: "Devices", activeValue: "device", changeValue: "device" },
  {
    label: "Apps",
    activeValue: "applications",
    changeValue: "applications",
  },
  {
    label: "Top Apps",
    activeValue: "topApplications",
    changeValue: "topApplications",
  },
  {
    label: "Active Vehicles",
    activeValue: "vehicleReport",
    changeValue: "vehicleReport",
  },
  {
    label: "Store Metrics",
    activeValue: "storeMetrics",
    changeValue: "storeMetrics",
  },
  {
    label: "Payments Summary",
    activeValue: "paymentSummary",
    changeValue: "paymentSummary",
    hideWhenIdpLogin: true,
    hidden: true,
  },
  {
    label: "Data Consumption",
    activeValue: "dataConsumption",
    changeValue: "dataConsumption",
  },
  { label: "Crash Report", activeValue: "appCrash", changeValue: "appCrash" },
];

const OPERATION_REPORTS_ITEMS = [
  {
    label: "Connected Vehicles",
    activeValue: "connectedDevices",
    changeValue: "connectedDevices",
  },
  {
    label: "Downloads",
    activeValue: "downloads",
    changeValue: "downloads",
  },
  {
    label: "Data Consumption",
    activeValue: "consumption",
    changeValue: "consumption",
  },
];

const FEATURE_ITEMS = [
  {
    label: "Features Activity",
    activeValue: "featuresActivity",
    changeValue: "featuresActivity",
  },
];

const SALES_ITEMS = [
  {
    label: "Subscriptions",
    activeValue: "Subscriptions",
    changeValue: "subscriptions",
  },
  {
    label: "In-app Purchase",
    activeValue: "In-app Purchase",
    changeValue: "inAppPurchase",
  },
];

function ReportsNavItem({ activeNavItem, item, handleActiveItemChange }) {
  const isActive = activeNavItem === item.activeValue;

  return (
    <button
      type="button"
      className={`reports-side-menu-item ${isActive ? "activeItem" : ""}`}
      onClick={() => handleActiveItemChange(item.changeValue)}
    >
      <Typography
        className="reports-side-menu-item-text"
        sx={{
          fontWeight: isActive ? "600" : "400",
          fontSize: "14px",
          color: isActive ? "#0476E2" : "#2A2C2D",
        }}
      >
        {item.label}
      </Typography>
    </button>
  );
}

ReportsNavItem.propTypes = {
  activeNavItem: PropTypes.string.isRequired,
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    activeValue: PropTypes.string.isRequired,
    changeValue: PropTypes.string.isRequired,
    hideWhenIdpLogin: PropTypes.bool,
  }).isRequired,
  handleActiveItemChange: PropTypes.func.isRequired,
};

function ReportsSection({
  title,
  isOpen,
  onToggle,
  items,
  activeNavItem,
  handleActiveItemChange,
  hideWhenIdpLogin,
}) {
  if (hideWhenIdpLogin) {
    return null;
  }

  return (
    <div className="nav-section">
      <button
        type="button"
        className="nav-header-global-admin"
        onClick={onToggle}
      >
        <Box display="flex" alignItems="center" sx={{ paddingLeft: "12px" }}>
          {isOpen ? (
            <ArrowDropUpRounded sx={{ color: "#12141599" }} />
          ) : (
            <ArrowDropDownRounded sx={{ color: "#12141599" }} />
          )}
          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
            {title}
          </Typography>
        </Box>
      </button>
      {isOpen && (
        <div className="nav-items">
          {items.map((item) => (
            <ReportsNavItem
              key={item.changeValue}
              activeNavItem={activeNavItem}
              item={item}
              handleActiveItemChange={handleActiveItemChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

ReportsSection.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      activeValue: PropTypes.string.isRequired,
      changeValue: PropTypes.string.isRequired,
      hideWhenIdpLogin: PropTypes.bool,
    })
  ).isRequired,
  activeNavItem: PropTypes.string.isRequired,
  handleActiveItemChange: PropTypes.func.isRequired,
  hideWhenIdpLogin: PropTypes.bool,
};

export function SideMenuReports({
  activeNavItem,
  handleActiveItemChange,
  tableauOpen,
  setTableauOpen,
}) {
  const [usageOpen, setUsageOpen] = useState(true);
  const [operationReportsOpen, setOperationReportsOpen] = useState(true);
  const isIdpLogin = sessionStorage.getItem("idpLogin") === "true";

  const handleTableauClick = () => {
    setTableauOpen(!tableauOpen);
  };

  const usageItems = USAGE_ITEMS.filter(
    (item) => !item.hidden && (!item.hideWhenIdpLogin || !isIdpLogin)
  );

  return (
    <div className="reports-side-menu-container">
      <button
        type="button"
        className={`reports-side-menu-item ${
          activeNavItem === "Application Summary" ? "activeItem" : ""
        }`}
        onClick={() => handleActiveItemChange("applicationSummary")}
      >
        <Typography
          className="reports-side-menu-item-text"
          sx={{
            color:
              activeNavItem === "Application Summary" ? "#0476E2" : "#2A2C2D",
            fontWeight: activeNavItem === "Application Summary" ? "600" : "400",
            paddingLeft: "26px !important",
          }}
        >
          Application Summary
        </Typography>
      </button>

      {/* Tableau Reports Dropdown */}
      <button
        type="button"
        className="reports-side-menu-item"
        onClick={handleTableauClick}
      >
        <Typography
          className="reports-side-menu-item-text"
          sx={{ paddingLeft: "0px !important" }}
        >
          <span>
            <ArrowDropDownRounded
              sx={{
                color: "#12141599",
                transform: tableauOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </span>{" "}
          Tableau Reports
        </Typography>
      </button>

      {/* Usage Section */}
      {tableauOpen && (
        <ReportsSection
          title="Usage"
          isOpen={usageOpen}
          onToggle={() => setUsageOpen(!usageOpen)}
          items={usageItems}
          activeNavItem={activeNavItem}
          handleActiveItemChange={handleActiveItemChange}
        />
      )}

      {/* Operation Reports Section */}
      {tableauOpen && (
        <ReportsSection
          title="Operation Reports"
          isOpen={operationReportsOpen}
          onToggle={() => setOperationReportsOpen(!operationReportsOpen)}
          items={OPERATION_REPORTS_ITEMS}
          activeNavItem={activeNavItem}
          handleActiveItemChange={handleActiveItemChange}
        />
      )}
    </div>
  );
}

SideMenuReports.propTypes = {
  activeNavItem: PropTypes.string.isRequired,
  handleActiveItemChange: PropTypes.func.isRequired,
  tableauOpen: PropTypes.bool.isRequired,
  setTableauOpen: PropTypes.func.isRequired,
};
